import React, { useState, useEffect, useMemo } from 'react';
import { User, Calendar, FileText, Upload, Download, Trash2, Plus, X, Video, ArrowLeft, Edit2, Save, Heart, Stethoscope, ChevronDown, ChevronUp, CreditCard, Search, Clock, Camera } from 'lucide-react';
import { getStatusLabel, getPaymentLabel, canPatientCancel, canJoinTeleconsultation } from './utils/appointmentStatus';
import { compressImage } from './utils/imageCompression';
import { formatDate } from './utils/formatDate';
import SerdiPayPayment from './SerdiPayPayment';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function UserAccount({ user, onLogout, onStartConsultation, onBack, onBooking }) {
  const [activeTab, setActiveTab] = useState('profile');
  const [appointments, setAppointments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [consultationNotes, setConsultationNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [expandedNote, setExpandedNote] = useState(null);
  const [paymentModal, setPaymentModal] = useState({ show: false, appointment: null, service: null });
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  
  // Notes filters
  const [notesSearch, setNotesSearch] = useState('');
  const [notesDoctor, setNotesDoctor] = useState('all');
  const [notesSortOrder, setNotesSortOrder] = useState('recent');
  
  const [profileData, setProfileData] = useState({
    gender: user?.gender || '',
    age: user?.age || '',
    date_of_birth: user?.date_of_birth || '',
    blood_type: user?.blood_type || '',
    allergies: user?.allergies || '',
    current_medications: user?.current_medications || '',
    medical_history: user?.medical_history || '',
    chronic_conditions: user?.chronic_conditions || '',
    emergency_contact: user?.emergency_contact || '',
    emergency_phone: user?.emergency_phone || ''
  });
  const [uploadData, setUploadData] = useState({
    document_type: 'prescription',
    document_name: '',
    notes: ''
  });

  const getAuthToken = () => localStorage.getItem('auth_token');

  useEffect(() => {
    if (activeTab === 'appointments') {
      fetchAppointments();
    } else if (activeTab === 'documents') {
      fetchDocuments();
    } else if (activeTab === 'notes') {
      fetchConsultationNotes();
    }
  }, [activeTab]);

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/appointments/my`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });
      const data = await response.json();
      // Sort appointments: most recent first
      const sortedAppointments = (data.appointments || []).sort((a, b) => {
        const dateA = new Date(`${a.appointment_date}T${a.appointment_time}`);
        const dateB = new Date(`${b.appointment_date}T${b.appointment_time}`);
        return dateB - dateA;
      });
      setAppointments(sortedAppointments);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/documents/my`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });
      const data = await response.json();
      setDocuments(data.documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConsultationNotes = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/patient/consultation-notes`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });
      const data = await response.json();
      setConsultationNotes(data.notes || []);
    } catch (error) {
      console.error('Error fetching consultation notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async (noteId) => {
    setDownloadingPdf(noteId);
    try {
      const response = await fetch(`${API_URL}/api/consultation-notes/${noteId}/pdf`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors du téléchargement');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `consultation_${noteId.substring(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Erreur lors du téléchargement du PDF');
    } finally {
      setDownloadingPdf(null);
    }
  };

  const handleUploadDocument = async () => {
    if (!uploadFile || !uploadData.document_name) {
      alert('Veuillez sélectionner un fichier et entrer un nom');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('document_type', uploadData.document_type);
    formData.append('document_name', uploadData.document_name);
    if (uploadData.notes) {
      formData.append('notes', uploadData.notes);
    }

    try {
      const response = await fetch(`${API_URL}/api/documents/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: formData
      });

      if (response.ok) {
        alert('Document téléversé avec succès');
        setShowUploadModal(false);
        setUploadFile(null);
        setUploadData({ document_type: 'prescription', document_name: '', notes: '' });
        fetchDocuments();
      } else {
        alert('Erreur lors du téléversement');
      }
    } catch (error) {
      alert('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  // Check if appointment needs payment
  const needsPayment = (apt) => {
    return apt.payment_status !== 'paid' && 
           apt.status !== 'cancelled' && 
           apt.status !== 'completed';
  };

  // Handle opening payment modal
  const openPaymentModal = (apt) => {
    setPaymentModal({
      show: true,
      appointment: apt,
      service: apt.service || { name: 'Consultation' }
    });
  };

  // Handle payment completion
  const handlePaymentComplete = () => {
    setPaymentModal({ show: false, appointment: null, service: null });
    fetchAppointments(); // Refresh appointments list
  };

  const openDocument = async (documentId, filename) => {
    try {
      const response = await fetch(`${API_URL}/api/documents/${documentId}/download`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });
      if (!response.ok) throw new Error('Accès refusé');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      // Open in a new tab so the browser previews it natively (PDF viewer,
      // image viewer) AND lets the user save via the browser controls.
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener';
      // Setting `download` here keeps a "Save As" hint on some browsers while
      // still opening the file in a new tab in others.
      a.download = filename;
      a.click();
      // Delay revocation so the new tab has time to load the blob URL.
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
      // Optimistically mark as viewed locally, then refresh
      setDocuments((docs) => docs.map((d) =>
        d.document_id === documentId && !d.patient_viewed_at
          ? { ...d, patient_viewed_at: new Date().toISOString() }
          : d
      ));
    } catch (error) {
      alert('Erreur lors de l’ouverture du document');
    }
  };

  const deleteDocument = async (documentId) => {
    if (!window.confirm('Supprimer ce document?')) return;

    try {
      await fetch(`${API_URL}/api/documents/${documentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });
      alert('Document supprimé');
      fetchDocuments();
    } catch (error) {
      alert('Erreur lors de la suppression');
    }
  };

  const cancelAppointment = async (appointmentId) => {
    if (!window.confirm('Annuler ce rendez-vous?')) return;

    try {
      await fetch(`${API_URL}/api/appointments/${appointmentId}/cancel`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });
      alert('Rendez-vous annulé');
      fetchAppointments();
    } catch (error) {
      alert('Erreur lors de l\'annulation');
    }
  };

  const saveProfile = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/user/profile/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify(profileData)
      });
      
      if (response.ok) {
        const data = await response.json();
        alert('Profil mis à jour avec succès');
        setEditingProfile(false);
        // Update local storage with new user data
        localStorage.setItem('user', JSON.stringify(data.user));
      } else {
        alert('Erreur lors de la mise à jour');
      }
    } catch (error) {
      alert('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const documentTypes = {
    'prescription': 'Ordonnance',
    'exam_request': 'Bon de Demande d\'Examens',
    'lab_result': 'Résultat d\'Analyse',
    'xray': 'Radiographie',
    'consultation_report': 'Rapport de Consultation',
    'certificate': 'Certificat Médical',
    'other': 'Autre'
  };

  const bloodTypes = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

  // Get unique doctors from notes
  const uniqueDoctors = useMemo(() => {
    const doctors = new Set();
    consultationNotes.forEach(note => {
      if (note.doctor_name) doctors.add(note.doctor_name);
    });
    return Array.from(doctors);
  }, [consultationNotes]);

  // Filter and sort notes
  const filteredNotes = useMemo(() => {
    let filtered = [...consultationNotes];
    
    // Search filter
    if (notesSearch.trim()) {
      const searchLower = notesSearch.toLowerCase();
      filtered = filtered.filter(note => {
        const doctorName = (note.doctor_name || '').toLowerCase();
        const diagnosis = (note.diagnosis || '').toLowerCase();
        const content = (note.content || '').toLowerCase();
        return doctorName.includes(searchLower) || 
               diagnosis.includes(searchLower) || 
               content.includes(searchLower);
      });
    }
    
    // Doctor filter
    if (notesDoctor !== 'all') {
      filtered = filtered.filter(note => note.doctor_name === notesDoctor);
    }
    
    // Sort
    filtered.sort((a, b) => {
      const dateA = new Date(a.created_at || a.consultation_date);
      const dateB = new Date(b.created_at || b.consultation_date);
      return notesSortOrder === 'recent' ? dateB - dateA : dateA - dateB;
    });
    
    return filtered;
  }, [consultationNotes, notesSearch, notesDoctor, notesSortOrder]);

  // Notes statistics
  const notesStats = useMemo(() => ({
    total: consultationNotes.length,
    doctors: uniqueDoctors.length,
    thisYear: consultationNotes.filter(note => {
      const noteDate = new Date(note.created_at || note.consultation_date);
      return noteDate.getFullYear() === new Date().getFullYear();
    }).length
  }), [consultationNotes, uniqueDoctors]);

  return (
    <div className="user-account">
      <div className="account-header">
        <div className="account-header-content">
          <div className="account-header-left">
            {onBack && (
              <button onClick={onBack} className="btn-back-icon" title="Retour à l'accueil">
                <ArrowLeft size={24} />
              </button>
            )}
            <div>
              <h1>Mon Compte</h1>
              <p>Bienvenue, {user.first_name} {user.last_name}</p>
            </div>
          </div>
          <div className="account-header-actions">
            {onBooking && (
              <button onClick={onBooking} className="btn-primary" data-testid="booking-btn">
                <Calendar size={18} />
                Prendre RDV
              </button>
            )}
            <button onClick={onLogout} className="btn-logout" data-testid="logout-btn">
              Se déconnecter
            </button>
          </div>
        </div>
      </div>

      <div className="account-nav">
        <button
          className={activeTab === 'profile' ? 'active' : ''}
          onClick={() => { setActiveTab('profile'); document.querySelector('.account-content')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
          data-testid="tab-profile"
        >
          <User size={20} /> Profil
        </button>
        <button
          className={activeTab === 'appointments' ? 'active' : ''}
          onClick={() => { setActiveTab('appointments'); document.querySelector('.account-content')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
          data-testid="tab-appointments"
        >
          <Calendar size={20} /> Mes Rendez-vous
        </button>
        <button
          className={activeTab === 'notes' ? 'active' : ''}
          onClick={() => { setActiveTab('notes'); document.querySelector('.account-content')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
          data-testid="tab-notes"
        >
          <Stethoscope size={20} /> Notes Médicales
        </button>
        <button
          className={activeTab === 'documents' ? 'active' : ''}
          onClick={() => { setActiveTab('documents'); document.querySelector('.account-content')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
          data-testid="tab-documents"
          style={{ position: 'relative' }}
        >
          <FileText size={20} /> Dossier Médical
          {(() => {
            const unread = documents.filter(d => d.uploaded_by?.type === 'doctor' && !d.patient_viewed_at).length;
            return unread > 0 ? (
              <span
                data-testid="documents-unread-count"
                style={{
                  marginLeft: 6,
                  background: '#dc2626',
                  color: '#fff',
                  fontSize: 11,
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: 12,
                  minWidth: 20,
                  display: 'inline-block',
                  textAlign: 'center',
                }}
              >
                {unread > 9 ? '9+' : unread}
              </span>
            ) : null;
          })()}
        </button>
      </div>

      <div className="account-content">
        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="profile-section" data-testid="profile-section">
            <div className="profile-header">
              <h2>Informations Personnelles</h2>
              <button 
                onClick={() => setEditingProfile(!editingProfile)} 
                className="btn-edit"
              >
                {editingProfile ? (
                  <>
                    <X size={18} />
                    <span>Annuler</span>
                  </>
                ) : (
                  <>
                    <Edit2 size={18} />
                    <span>Modifier</span>
                  </>
                )}
              </button>
            </div>
            
            <div className="profile-info">
              <div className="info-item">
                <label>Nom Complet</label>
                <p>{user.first_name} {user.last_name}</p>
              </div>
              <div className="info-item">
                <label>Téléphone</label>
                <p>{user.phone_number}</p>
              </div>
              <div className="info-item">
                <label>Email</label>
                <p>{user.email || 'Non renseigné'}</p>
              </div>
              <div className="info-item">
                <label>Ville</label>
                <p>{user.city || 'Non renseigné'}</p>
              </div>
            </div>

            {/* Medical Profile Section */}
            <div className="medical-profile-section">
              <h3>
                <Heart size={20} />
                <span>Informations Médicales</span>
              </h3>
              
              {editingProfile ? (
                <div className="medical-form" key="edit-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Sexe</label>
                      <select
                        value={profileData.gender}
                        onChange={(e) => setProfileData({...profileData, gender: e.target.value})}
                      >
                        <option value="">Sélectionner...</option>
                        <option value="M">Masculin</option>
                        <option value="F">Féminin</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Âge</label>
                      <input
                        type="number"
                        value={profileData.age}
                        onChange={(e) => setProfileData({...profileData, age: e.target.value})}
                        placeholder="Ex: 35"
                        min="0"
                        max="120"
                      />
                    </div>
                    <div className="form-group">
                      <label>Groupe Sanguin</label>
                      <select
                        value={profileData.blood_type}
                        onChange={(e) => setProfileData({...profileData, blood_type: e.target.value})}
                      >
                        <option value="">Sélectionner...</option>
                        {bloodTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label>Allergies connues</label>
                    <textarea
                      value={profileData.allergies}
                      onChange={(e) => setProfileData({...profileData, allergies: e.target.value})}
                      placeholder="Ex: Pénicilline, arachides..."
                      rows={2}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Médicaments actuels</label>
                    <textarea
                      value={profileData.current_medications}
                      onChange={(e) => setProfileData({...profileData, current_medications: e.target.value})}
                      placeholder="Liste des médicaments que vous prenez actuellement..."
                      rows={2}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Antécédents médicaux</label>
                    <textarea
                      value={profileData.medical_history}
                      onChange={(e) => setProfileData({...profileData, medical_history: e.target.value})}
                      placeholder="Chirurgies, hospitalisations, maladies passées..."
                      rows={3}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Maladies chroniques</label>
                    <textarea
                      value={profileData.chronic_conditions}
                      onChange={(e) => setProfileData({...profileData, chronic_conditions: e.target.value})}
                      placeholder="Ex: Diabète, hypertension, asthme..."
                      rows={2}
                    />
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Contact d'urgence</label>
                      <input
                        type="text"
                        value={profileData.emergency_contact}
                        onChange={(e) => setProfileData({...profileData, emergency_contact: e.target.value})}
                        placeholder="Nom du contact"
                      />
                    </div>
                    <div className="form-group">
                      <label>Téléphone d'urgence</label>
                      <input
                        type="tel"
                        value={profileData.emergency_phone}
                        onChange={(e) => setProfileData({...profileData, emergency_phone: e.target.value})}
                        placeholder="+243..."
                      />
                    </div>
                  </div>
                  
                  <button onClick={saveProfile} className="btn-primary btn-save" disabled={loading}>
                    <Save size={18} />
                    <span>{loading ? 'Enregistrement...' : 'Enregistrer'}</span>
                  </button>
                </div>
              ) : (
                <div className="medical-info" key="display-info">
                  <div className="info-row">
                    <div className="info-item">
                      <label>Sexe</label>
                      <p>{profileData.gender === 'M' ? 'Masculin' : profileData.gender === 'F' ? 'Féminin' : 'Non renseigné'}</p>
                    </div>
                    <div className="info-item">
                      <label>Âge</label>
                      <p>{profileData.age ? `${profileData.age} ans` : 'Non renseigné'}</p>
                    </div>
                    <div className="info-item">
                      <label>Groupe Sanguin</label>
                      <p>{profileData.blood_type || 'Non renseigné'}</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <label>Allergies</label>
                    <p>{profileData.allergies || 'Aucune allergie connue'}</p>
                  </div>
                  <div className="info-item">
                    <label>Médicaments actuels</label>
                    <p>{profileData.current_medications || 'Aucun'}</p>
                  </div>
                  <div className="info-item">
                    <label>Antécédents médicaux</label>
                    <p>{profileData.medical_history || 'Non renseigné'}</p>
                  </div>
                  <div className="info-item">
                    <label>Maladies chroniques</label>
                    <p>{profileData.chronic_conditions || 'Aucune'}</p>
                  </div>
                  <div className="info-row">
                    <div className="info-item">
                      <label>Contact d'urgence</label>
                      <p>{profileData.emergency_contact || 'Non renseigné'}</p>
                    </div>
                    <div className="info-item">
                      <label>Téléphone d'urgence</label>
                      <p>{profileData.emergency_phone || 'Non renseigné'}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Appointments Tab */}
        {activeTab === 'appointments' && (
          <div className="appointments-section" data-testid="appointments-section">
            <h2>Mes Rendez-vous</h2>
            {loading ? (
              <p>Chargement...</p>
            ) : appointments.length === 0 ? (
              <p className="empty-state">Aucun rendez-vous</p>
            ) : (
              <div className="appointments-list">
                {appointments.map((apt) => (
                  <div key={apt.appointment_id} className="appointment-card">
                    <div className="appointment-header">
                      <div>
                        <h3>{apt.service?.name}</h3>
                        <p>{apt.doctor?.name} - {apt.doctor?.specialty}</p>
                      </div>
                      <div className={`status-badge status-${apt.status}`}>
                        {getStatusLabel(apt.status)}
                      </div>
                    </div>
                    <div className="appointment-details">
                      <div><Calendar size={18} /> {formatDate(apt.appointment_date)}</div>
                      <div>{apt.appointment_time}</div>
                      <div className={`payment-status payment-${apt.payment_status}`}>
                        {getPaymentLabel(apt.payment_status)}
                      </div>
                    </div>
                    <div className="appointment-actions">
                      {/* Payment Button - If not paid */}
                      {needsPayment(apt) && (
                        <button
                          onClick={() => openPaymentModal(apt)}
                          className="btn-pay"
                          data-testid={`pay-btn-${apt.appointment_id}`}
                        >
                          <CreditCard size={18} /> Payer
                        </button>
                      )}
                      {/* Teleconsultation Button - Patient can join if confirmed or in-progress */}
                      {canJoinTeleconsultation(apt.status, apt.service) && onStartConsultation && (
                        <button
                          onClick={async () => {
                            // Notify the other party (doctor) about the incoming call
                            try {
                              const token = getAuthToken();
                              await fetch(`${API_URL}/api/video/call/initiate`, {
                                method: 'POST',
                                headers: {
                                  'Authorization': `Bearer ${token}`,
                                  'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({ appointment_id: apt.appointment_id })
                              });
                            } catch (e) {
                              console.warn('Could not notify other party:', e);
                            }
                            // Start the consultation
                            onStartConsultation(apt);
                          }}
                          className="btn-teleconsultation"
                        >
                          <Video size={18} /> {apt.status === 'in-progress' ? 'Rejoindre' : 'Démarrer'} Téléconsultation
                        </button>
                      )}
                      {/* Cancel Button - Patient can cancel if not in-progress or completed */}
                      {canPatientCancel(apt.status) && (
                        <button
                          onClick={() => cancelAppointment(apt.appointment_id)}
                          className="btn-cancel"
                        >
                          Annuler
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Consultation Notes Tab */}
        {activeTab === 'notes' && (
          <div className="notes-section" data-testid="notes-section">
            <h2>Mes Notes de Consultation</h2>
            <p className="section-description">
              Retrouvez ici les comptes-rendus de vos consultations validés par vos médecins.
            </p>
            
            {loading ? (
              <p>Chargement...</p>
            ) : consultationNotes.length === 0 ? (
              <div className="empty-state-box">
                <Stethoscope size={48} />
                <p>Aucune note de consultation</p>
                <span>Les notes apparaîtront ici après validation par votre médecin</span>
              </div>
            ) : (
              <>
                {/* Statistics */}
                <div className="notes-stats" style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                  gap: '12px',
                  marginBottom: '20px'
                }}>
                  <div style={{
                    background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                    borderRadius: '12px',
                    padding: '16px',
                    color: 'white',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{notesStats.total}</div>
                    <div style={{ fontSize: '0.8rem', opacity: 0.9 }}>Consultations</div>
                  </div>
                  <div style={{
                    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                    borderRadius: '12px',
                    padding: '16px',
                    color: 'white',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{notesStats.thisYear}</div>
                    <div style={{ fontSize: '0.8rem', opacity: 0.9 }}>Cette année</div>
                  </div>
                  <div style={{
                    background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                    borderRadius: '12px',
                    padding: '16px',
                    color: 'white',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{notesStats.doctors}</div>
                    <div style={{ fontSize: '0.8rem', opacity: 0.9 }}>Médecins</div>
                  </div>
                </div>

                {/* Filters */}
                <div className="notes-filters" style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '12px',
                  marginBottom: '20px',
                  padding: '16px',
                  background: '#f8fafc',
                  borderRadius: '12px'
                }}>
                  {/* Search */}
                  <div style={{ flex: '1 1 200px', position: 'relative' }}>
                    <Search size={18} style={{ 
                      position: 'absolute', 
                      left: '12px', 
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      color: '#94a3b8'
                    }} />
                    <input
                      type="text"
                      placeholder="Rechercher dans les notes..."
                      value={notesSearch}
                      onChange={(e) => setNotesSearch(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px 14px 10px 40px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '0.95rem'
                      }}
                      data-testid="notes-search"
                    />
                  </div>
                  
                  {/* Doctor Filter */}
                  <select
                    value={notesDoctor}
                    onChange={(e) => setNotesDoctor(e.target.value)}
                    style={{
                      padding: '10px 14px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      background: 'white',
                      cursor: 'pointer',
                      minWidth: '180px'
                    }}
                    data-testid="notes-doctor-filter"
                  >
                    <option value="all">Tous les médecins</option>
                    {uniqueDoctors.map(doc => (
                      <option key={doc} value={doc}>{doc}</option>
                    ))}
                  </select>
                  
                  {/* Sort */}
                  <select
                    value={notesSortOrder}
                    onChange={(e) => setNotesSortOrder(e.target.value)}
                    style={{
                      padding: '10px 14px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      background: 'white',
                      cursor: 'pointer'
                    }}
                    data-testid="notes-sort"
                  >
                    <option value="recent">Plus récent</option>
                    <option value="oldest">Plus ancien</option>
                  </select>
                </div>

                {/* Results count */}
                <div style={{ 
                  marginBottom: '16px', 
                  color: '#64748b', 
                  fontSize: '0.9rem' 
                }}>
                  {filteredNotes.length} note{filteredNotes.length > 1 ? 's' : ''} trouvée{filteredNotes.length > 1 ? 's' : ''}
                  {notesSearch && ` pour "${notesSearch}"`}
                </div>

                {filteredNotes.length === 0 ? (
                  <div className="empty-state-box">
                    <Search size={48} />
                    <p>Aucun résultat</p>
                    <span>Essayez de modifier vos filtres</span>
                    <button 
                      onClick={() => {
                        setNotesSearch('');
                        setNotesDoctor('all');
                      }}
                      style={{
                        marginTop: '12px',
                        padding: '8px 16px',
                        background: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer'
                      }}
                    >
                      Réinitialiser les filtres
                    </button>
                  </div>
                ) : (
                  <div className="consultation-notes-list">
                    {filteredNotes.map((note) => (
                      <div key={note.note_id} className="consultation-note-card">
                        <div 
                          className="note-card-header"
                          onClick={() => setExpandedNote(expandedNote === note.note_id ? null : note.note_id)}
                        >
                          <div className="note-card-info">
                            <h3>{note.service_name || 'Consultation'}</h3>
                            <div className="note-meta">
                              <span><Calendar size={16} /> {formatDate(note.appointment_date)} à {note.appointment_time}</span>
                              <span><User size={16} /> {note.doctor_name?.startsWith('Dr.') ? note.doctor_name : `Dr. ${note.doctor_name}`}</span>
                            </div>
                          </div>
                          <div className="note-card-toggle">
                            {expandedNote === note.note_id ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                          </div>
                        </div>
                        
                        {expandedNote === note.note_id && (
                          <div className="note-card-content">
                            {/* Subjective */}
                            <div className="soap-display-section">
                              <h4 className="soap-title soap-s">S - Subjectif (Anamnèse)</h4>
                              <div className="soap-content">
                                {note.soap_note?.subjective || <span className="text-muted">Non renseigné</span>}
                              </div>
                            </div>

                            {/* Objective */}
                            <div className="soap-display-section">
                              <h4 className="soap-title soap-o">O - Objectif (Examen)</h4>
                              <div className="soap-content">
                                {note.soap_note?.objective || <span className="text-muted">Non renseigné</span>}
                              </div>
                            </div>

                            {/* Assessment */}
                            <div className="soap-display-section">
                              <h4 className="soap-title soap-a">A - Assessment (Diagnostic)</h4>
                              <div className="soap-content">
                                {note.soap_note?.assessment || <span className="text-muted">Non renseigné</span>}
                              </div>
                            </div>

                            {/* Plan */}
                            <div className="soap-display-section">
                              <h4 className="soap-title soap-p">P - Plan (Traitement)</h4>
                              <div className="soap-content">
                                {note.soap_note?.plan || <span className="text-muted">Non renseigné</span>}
                              </div>
                            </div>

                            <div className="note-footer">
                              <span className="validated-date">
                                Validé le {formatDate(note.validated_at || note.created_at)}
                              </span>
                              <button 
                                className="download-pdf-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadPdf(note.note_id);
                                }}
                                disabled={downloadingPdf === note.note_id}
                              >
                                {downloadingPdf === note.note_id ? (
                                  <>
                                    <span className="spinner-small"></span>
                                    Téléchargement...
                                  </>
                                ) : (
                                  <>
                                    <Download size={16} />
                                    Télécharger PDF
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div className="documents-section" data-testid="documents-section">
            <div className="section-header">
              <h2>Mon Dossier Médical</h2>
              <button
                onClick={() => setShowUploadModal(true)}
                className="btn-primary"
                data-testid="upload-doc-btn"
              >
                <Plus size={20} /> Ajouter un Document
              </button>
            </div>

            {loading ? (
              <p>Chargement...</p>
            ) : documents.length === 0 ? (
              <div className="dossier-empty">
                <FileText size={44} />
                <p>Aucun document dans votre dossier</p>
                <span>Les documents déposés par votre médecin apparaîtront ici après consultation.</span>
              </div>
            ) : (() => {
              const doctorUnread = documents.filter(d => d.uploaded_by?.type === 'doctor' && !d.patient_viewed_at);
              const doctorRead = documents.filter(d => d.uploaded_by?.type === 'doctor' && d.patient_viewed_at);
              const patientDocs = documents.filter(d => d.uploaded_by?.type !== 'doctor');

              const renderCard = (doc) => {
                const isUnread = doc.uploaded_by?.type === 'doctor' && !doc.patient_viewed_at;
                const isDoctor = doc.uploaded_by?.type === 'doctor';
                return (
                  <div
                    key={doc.document_id}
                    className={`document-card ${isDoctor ? 'doc-by-doctor' : 'doc-by-patient'} ${isUnread ? 'doc-unread' : ''}`}
                    data-testid={`document-card-${doc.document_id}`}
                    style={{ position: 'relative', ...(isUnread ? {
                      boxShadow: '0 0 0 2px #4A90A4, 0 4px 12px rgba(74, 144, 164, 0.25)',
                      background: 'linear-gradient(180deg, #eff8fc 0%, #ffffff 60%)',
                    } : {}) }}
                  >
                    {isUnread && (
                      <span
                        data-testid={`unread-badge-${doc.document_id}`}
                        style={{
                          position: 'absolute', top: 10, right: 10,
                          background: '#dc2626', color: '#fff',
                          fontSize: 11, fontWeight: 700,
                          padding: '3px 8px', borderRadius: 12,
                          letterSpacing: 0.3,
                        }}
                      >
                        NOUVEAU
                      </span>
                    )}
                    <div className="document-icon">
                      <FileText size={40} />
                    </div>
                    {isDoctor ? (
                      <span className="doc-badge doc-badge-doctor"><Stethoscope size={11} /> Médecin</span>
                    ) : (
                      <span className="doc-badge doc-badge-patient"><User size={11} /> Vous</span>
                    )}
                    <h3>{doc.document_name}</h3>
                    <p className="document-type">{documentTypes[doc.document_type]}</p>
                    <p className="document-date">
                      {formatDate(doc.uploaded_at)}
                    </p>
                    {doc.uploaded_by?.name && (
                      <p className="document-author">Par: {doc.uploaded_by.name}</p>
                    )}
                    {doc.notes && <p className="document-notes">{doc.notes}</p>}
                    <div className="document-actions">
                      <button
                        onClick={() => openDocument(doc.document_id, doc.original_filename)}
                        className="btn-icon"
                        title="Ouvrir / Télécharger"
                        data-testid={`open-doc-${doc.document_id}`}
                      >
                        <Download size={18} />
                      </button>
                      {!doc.uploaded_by?.type && (
                        <button
                          onClick={() => deleteDocument(doc.document_id)}
                          className="btn-icon btn-danger"
                          title="Supprimer"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              };

              return (
                <div className="dossier-sections" data-testid="dossier-sections">
                  {doctorUnread.length > 0 && (
                    <section className="dossier-section dossier-section-unread" data-testid="dossier-doctor-unread">
                      <div className="dossier-section-header">
                        <span className="dossier-section-dot dossier-dot-unread"></span>
                        <h3>Nouveaux documents de votre médecin</h3>
                        <span className="dossier-section-count">{doctorUnread.length}</span>
                      </div>
                      <p className="dossier-section-hint">À consulter — non encore ouverts.</p>
                      <div className="documents-grid">
                        {doctorUnread.map(renderCard)}
                      </div>
                    </section>
                  )}

                  {doctorRead.length > 0 && (
                    <section className="dossier-section dossier-section-doctor" data-testid="dossier-doctor-read">
                      <div className="dossier-section-header">
                        <span className="dossier-section-dot dossier-dot-doctor"></span>
                        <h3>Documents de votre médecin</h3>
                        <span className="dossier-section-count">{doctorRead.length}</span>
                      </div>
                      <p className="dossier-section-hint">Ordonnances, résultats et rapports déjà consultés.</p>
                      <div className="documents-grid">
                        {doctorRead.map(renderCard)}
                      </div>
                    </section>
                  )}

                  {patientDocs.length > 0 && (
                    <section className="dossier-section dossier-section-patient" data-testid="dossier-patient-docs">
                      <div className="dossier-section-header">
                        <span className="dossier-section-dot dossier-dot-patient"></span>
                        <h3>Mes documents</h3>
                        <span className="dossier-section-count">{patientDocs.length}</span>
                      </div>
                      <p className="dossier-section-hint">Documents que vous avez déposés vous-même.</p>
                      <div className="documents-grid">
                        {patientDocs.map(renderCard)}
                      </div>
                    </section>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Ajouter un Document</h2>
              <button onClick={() => setShowUploadModal(false)} className="modal-close">
                <X size={24} />
              </button>
            </div>
            <div className="modal-form">
              <div className="form-group">
                <label>Type de Document</label>
                <select
                  value={uploadData.document_type}
                  onChange={(e) => setUploadData({...uploadData, document_type: e.target.value})}
                >
                  {Object.entries(documentTypes).map(([key, value]) => (
                    <option key={key} value={key}>{value}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Nom du Document</label>
                <input
                  type="text"
                  value={uploadData.document_name}
                  onChange={(e) => setUploadData({...uploadData, document_name: e.target.value})}
                  placeholder="Ex: Ordonnance Dr. Martin"
                />
              </div>
              <div className="form-group">
                <label>Notes (optionnel)</label>
                <textarea
                  value={uploadData.notes}
                  onChange={(e) => setUploadData({...uploadData, notes: e.target.value})}
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label>Fichier</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <label
                    htmlFor="patient-upload-file"
                    className="btn-secondary"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer', padding: '0.6rem', margin: 0 }}
                    data-testid="patient-choose-file-label"
                  >
                    <Upload size={18} /> Choisir un fichier
                  </label>
                  <input
                    id="patient-upload-file"
                    type="file"
                    onChange={async (e) => setUploadFile(await compressImage(e.target.files[0]))}
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    style={{ display: 'none' }}
                    data-testid="patient-upload-file"
                  />
                  <label
                    htmlFor="patient-upload-camera"
                    className="btn-secondary"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer', padding: '0.6rem', margin: 0 }}
                    data-testid="patient-take-photo-label"
                  >
                    <Camera size={18} /> Prendre une photo
                  </label>
                  <input
                    id="patient-upload-camera"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={async (e) => setUploadFile(await compressImage(e.target.files[0]))}
                    style={{ display: 'none' }}
                    data-testid="patient-upload-camera"
                  />
                  {uploadFile && (
                    <p style={{ fontSize: 13, color: '#475569', margin: '4px 0 0' }} data-testid="patient-selected-file">
                      Fichier sélectionné : <strong>{uploadFile.name}</strong> ({Math.round(uploadFile.size / 1024)} Ko)
                    </p>
                  )}
                </div>
              </div>
              <div className="modal-actions">
                <button onClick={() => setShowUploadModal(false)} className="btn-secondary">
                  Annuler
                </button>
                <button
                  onClick={handleUploadDocument}
                  disabled={loading}
                  className="btn-primary"
                >
                  {loading ? 'Envoi...' : 'Téléverser'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SerdiPay Payment Modal */}
      {paymentModal.show && (
        <div className="modal-overlay" data-testid="payment-modal">
          <div className="payment-modal-content">
            <button 
              className="modal-close" 
              onClick={() => setPaymentModal({ show: false, appointment: null, service: null })}
            >
              <X size={24} />
            </button>
            <SerdiPayPayment
              appointment={paymentModal.appointment}
              service={paymentModal.service}
              onPaymentComplete={handlePaymentComplete}
              onCancel={() => setPaymentModal({ show: false, appointment: null, service: null })}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default UserAccount;
